import React, { useEffect, useState } from 'react';
import { ChevronLeft, Clock, Ban, CheckCircle2, Info, Delete } from 'lucide-react';
import { getCouponInfo, usedCoupon } from "../../api/advanced_coupon/advanced.api";
import { useSearchParams } from "react-router-dom";
import { Cookies } from "react-cookie";

const CouponUsagePage = () => {
    const [couponStatus, setCouponStatus] = useState('ACTIVE');
    const [isProcessing, setIsProcessing] = useState(false);
    const [query] = useSearchParams();

    const [coupon, setCoupon] = useState({});
    const id = query.get("id");

    // 비밀번호 모달 및 키패드 관련 상태
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [isError, setIsError] = useState(false);
    const PIN_LENGTH = 4;

    useEffect(() => {
        const cookies = new Cookies();
        if (id) {
            getCouponInfo(id, cookies.get("omoplay_coupon_login_code")).then(({ data }) => {
                if (data === "login") {
                    cookies.remove("omoplay_coupon_login_code", { path: "/" });
                    alert("로그인이 필요합니다.");
                    window.location.href = "/advanced/coupon";
                } else if (data === "not-found") {
                    alert("쿠폰이 존재하지 않습니다.");
                    window.location.href = "/advanced/coupon";
                } else {
                    setCoupon(data);
                    setCouponStatus(data.status);
                }
            }).catch(() => {
                alert("쿠폰이 존재하지 않습니다.")
                window.location.href = "/advanced/coupon";
            })
        } else {
            window.location.href = "/advanced/coupon";
        }
    }, [id]);

    const handleUseCouponClick = () => {
        setShowPasswordModal(true);
        setPassword('');
        setIsError(false);
    };

    const handleNumberClick = (num) => {
        if (isError || password.length >= PIN_LENGTH) return;

        const newPassword = password + num;
        setPassword(newPassword);

        if (newPassword.length === PIN_LENGTH) {
            setTimeout(() => verifyPassword(newPassword), 100);
        }
    };

    const handleBackspace = () => {
        if (isError) return;
        setPassword((prev) => prev.slice(0, -1));
    };

    const verifyPassword = (pwd) => {
        if (pwd === "1234") {
            setIsProcessing(true);

            usedCoupon(id).then(() => {
                setCouponStatus('USED');
                setIsProcessing(false);
                setShowPasswordModal(false);
                alert("쿠폰 사용이 완료되었습니다.");
                setPassword('');
            }).catch(() => {
                setIsProcessing(false);
                alert("처리 중 오류가 발생했습니다.");
                setPassword('');
            });
        } else {
            setIsError(true);
            setTimeout(() => {
                setIsError(false);
                setPassword('');
            }, 500);
        }
    };

    const getStatusUI = () => {
        switch (couponStatus) {
            case 'USED':
                return {
                    icon: <CheckCircle2 size={48} className="text-white mb-2" />,
                    title: "쿠폰이 사용되었습니다",
                    subTitle: `사용 일시: ${coupon.usedAt}`,
                    bgColor: "bg-gray-800/80",
                    blur: false,
                    btnText: "사용 완료된 쿠폰",
                    btnDisabled: true,
                    btnColor: "bg-gray-300 text-gray-500"
                };
            case 'EXPIRED':
                return {
                    icon: <Clock size={48} className="text-white mb-2" />,
                    title: "쿠폰 기간이 만료되었습니다",
                    subTitle: "유효기간이 지난 쿠폰입니다.",
                    bgColor: "bg-gray-800/80",
                    blur: false,
                    btnText: "기간 만료",
                    btnDisabled: true,
                    btnColor: "bg-gray-300 text-gray-500"
                };
            case 'REFUNDED':
                return {
                    icon: <Ban size={48} className="text-white mb-2" />,
                    title: "쿠폰을 사용할 수 없습니다",
                    subTitle: "환불되었거나 비활성화된 쿠폰입니다.",
                    bgColor: "bg-gray-800/80",
                    blur: false,
                    btnText: "사용 불가",
                    btnDisabled: true,
                    btnColor: "bg-gray-300 text-gray-500"
                };
            case 'ACTIVE':
                return {
                    icon: null,
                    title: "",
                    subTitle: "",
                    bgColor: "bg-transparent",
                    blur: false,
                    btnText: "매장에서 사용하기",
                    btnDisabled: false,
                    btnColor: "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200"
                };
            default:
                return {};
        }
    };

    const uiConfig = getStatusUI();

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center font-sans text-gray-800 relative">
            <div className="w-full max-w-md bg-white shadow-xl min-h-screen flex flex-col relative">

                {/* --- [헤더] --- */}
                <header className="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-100 sticky top-0 z-20">
                    <button onClick={() => window.location.href = "/advanced/coupon"} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
                        <ChevronLeft size={24} />
                    </button>
                    <h1 className="text-lg font-bold text-gray-900">쿠폰 사용</h1>
                    <div className="w-10"></div>
                </header>

                {/* --- [메인 컨텐츠] --- */}
                <main className="flex-1 p-6 flex flex-col items-center">

                    {/* 1. 쿠폰 이미지 영역 */}
                    <div className="w-full aspect-[3/4] rounded-3xl overflow-hidden relative shadow-md border border-gray-100 mb-6 group">
                        <img
                            src={coupon.imageUrl}
                            alt="쿠폰 이미지"
                            className={`w-full h-full object-cover transition-all duration-500 ${uiConfig.blur ? 'blur-xl scale-110' : ''}`}
                        />

                        {/* 신규: ACTIVE 상태일 때 '사용가능' 초록색 뱃지 표시 */}
                        {couponStatus === 'ACTIVE' && (
                            <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-gray-100 px-3 py-1.5 rounded-full shadow-sm">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-green-600 font-extrabold text-sm tracking-tight">사용가능</span>
                            </div>
                        )}

                        {/* 비활성 상태(사용완료, 만료 등)일 때의 검은 오버레이 */}
                        {couponStatus !== 'ACTIVE' && (
                            <div className={`absolute inset-0 flex flex-col items-center justify-center text-center p-6 transition-all duration-300 ${uiConfig.bgColor}`}>
                                <div className="transform transition-transform duration-300 group-hover:scale-110">
                                    {uiConfig.icon}
                                </div>
                                <h2 className="text-xl font-bold text-white mb-2 leading-tight">
                                    {uiConfig.title}
                                </h2>
                                <p className="text-sm text-gray-200 font-medium">
                                    {uiConfig.subTitle}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="w-full text-center space-y-2 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                            {coupon.name}
                        </h2>
                        <p className="text-gray-500 text-sm">
                            매장에서 직원에게 이 화면을 보여주세요.
                        </p>
                    </div>

                    <div className="w-full bg-gray-50 rounded-xl p-5 border border-gray-100 text-left">
                        <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                            <span className="text-sm text-gray-500">유효기간</span>
                            <span className={`text-sm font-bold ${couponStatus === 'EXPIRED' ? 'text-red-500' : 'text-gray-900'}`}>
                                ~ {coupon.expiredAt} 까지
                            </span>
                        </div>
                        <div className="flex items-start gap-2">
                            <Info size={16} className="text-gray-400 mt-0.5 flex-shrink-0"/>
                            <ul className="text-xs text-gray-500 space-y-1">
                                <li>매장 상황에 따라 재료 소진 시 사용이 제한될 수 있습니다.</li>
                                <li>직원 확인 후 사용된 쿠폰은 취소가 불가능합니다.</li>
                                <li>캡쳐된 이미지는 사용이 불가할 수 있습니다.</li>
                            </ul>
                        </div>
                    </div>
                </main>

                <div className="sticky bottom-0 w-full bg-white border-t border-gray-200 p-4 safe-area-bottom z-30">
                    <button
                        onClick={handleUseCouponClick}
                        disabled={uiConfig.btnDisabled || isProcessing}
                        className={`w-full py-4 rounded-xl text-lg font-bold transition-all flex items-center justify-center gap-2 ${uiConfig.btnColor} ${isProcessing ? 'opacity-80 cursor-wait' : ''}`}
                    >
                        {isProcessing ? "처리 중..." : uiConfig.btnText}
                    </button>
                </div>
            </div>

            {/* --- [모바일 키패드 모달] --- */}
            {showPasswordModal && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60 sm:items-center sm:justify-center">
                    <div className="w-full sm:max-w-sm animate-fade-in-up">
                        <div className={`bg-white w-full rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl ${isError ? 'animate-shake' : ''}`}>
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold mb-2 text-gray-900">
                                    매장 직원 확인
                                </h3>
                                <p className="text-sm text-gray-500">
                                    직원이 확인 비밀번호(4자리)를 입력합니다.
                                </p>
                            </div>

                            <div className="flex justify-center gap-4 mb-8">
                                {[0, 1, 2, 3].map((index) => (
                                    <div
                                        key={index}
                                        className={`w-4 h-4 rounded-full transition-colors duration-200 ${
                                            isError ? 'bg-red-500' : (password.length > index ? 'bg-blue-600' : 'bg-gray-200')
                                        }`}
                                    />
                                ))}
                            </div>

                            <div className="grid grid-cols-3 gap-y-2 gap-x-2">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                    <button
                                        key={num}
                                        onClick={() => handleNumberClick(num.toString())}
                                        className={`py-4 text-2xl font-semibold rounded-2xl transition-colors ${
                                            isError ? 'text-red-500 bg-red-50' : 'text-gray-800 hover:bg-gray-100 active:bg-gray-200'
                                        }`}
                                    >
                                        {num}
                                    </button>
                                ))}
                                <button
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setPassword('');
                                        setIsError(false);
                                    }}
                                    className="py-4 text-lg font-medium text-gray-500 rounded-2xl hover:bg-gray-100 active:bg-gray-200 transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={() => handleNumberClick('0')}
                                    className={`py-4 text-2xl font-semibold rounded-2xl transition-colors ${
                                        isError ? 'text-red-500 bg-red-50' : 'text-gray-800 hover:bg-gray-100 active:bg-gray-200'
                                    }`}
                                >
                                    0
                                </button>
                                <button
                                    onClick={handleBackspace}
                                    className={`py-4 flex items-center justify-center rounded-2xl transition-colors ${
                                        isError ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                                    }`}
                                >
                                    <Delete size={28} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CSS 애니메이션 정의 --- */}
            <style>{`
                @keyframes fade-in-up {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.3s ease-out forwards;
                }

                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                    20%, 40%, 60%, 80% { transform: translateX(5px); }
                }
                .animate-shake {
                    animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
                }
            `}</style>
        </div>
    );
};

export default CouponUsagePage;